/* 動態解析度。

   為什麼是這一項：模擬層實測只佔一幀預算的 0.20%（34 µs/步），draw call 在
   0.2 的設計下約 100–300 個，兩者都不是瓶頸。真正會吃掉幀預算的是 fragment ——
   原本像素比鎖在 min(devicePixelRatio, 2)，在 Retina 上等於算四倍的像素，
   而這個遊戲的畫面是大面積的純色平面，fragment 成本幾乎全花在「多算的那三倍」上。

   做法：看幀時間的 p95（不是平均，掉幀是尖峰造成的），超過預算就降一級，
   明顯有餘裕才升一級。升降之間留很寬的死區，避免在邊界來回抖動 ——
   解析度反覆變化比固定在低解析度更難看。 */

import { perf } from './perf';
import { resizePostFX } from '../render/postfx';

const LEVELS = [1.0, 1.25, 1.5, 1.75, 2.0];

const DOWN_MS = 18.0;   // p95 超過這個就降級（60fps 的預算是 16.7）
const UP_MS = 11.0;     // p95 低於這個才升級，死區 7ms 很寬，刻意的
const COOLDOWN = 90;    // 兩次調整至少間隔這麼多幀，讓統計重新穩定

let idx = 0;
let maxIdx = 0;
let cooldown = 0;
let enabled = true;
let target: any = null;

export function initResolution(renderer: any) {
  target = renderer;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  maxIdx = LEVELS.reduce((best, v, i) => (v <= dpr + 1e-6 ? i : best), 0);
  idx = maxIdx;
  apply();
}

function apply() {
  if (!target) return;
  target.setPixelRatio(LEVELS[idx]);
  // setPixelRatio 之後要重新設定尺寸才會生效
  const c = target.domElement;
  if (c && c.parentElement) {
    target.setSize(c.parentElement.clientWidth || 1, c.parentElement.clientHeight || 1, false);
  }
  /* 後製鏈也要跟著改像素比，否則降級之後畫面會被拉伸取樣（見 postfx.ts 檔頭第 3 點）。 */
  resizePostFX();
}

/** 每幀呼叫。沒開監測時也要能運作，所以自己讀 perf 的快照而不依賴面板。 */
export function updateResolution() {
  if (!enabled || !target) return;
  if (cooldown > 0) { cooldown--; return; }

  const s = perf.snapshot();
  if (s.samples < 60) return;          // 樣本不足不要亂動

  if (s.frame.p95 > DOWN_MS && idx > 0) {
    idx--; apply(); cooldown = COOLDOWN;
  } else if (s.frame.p95 < UP_MS && idx < maxIdx) {
    idx++; apply(); cooldown = COOLDOWN;
  }
}

export function resolutionInfo() {
  return { ratio: LEVELS[idx], max: LEVELS[maxIdx], auto: enabled };
}

/** 讓使用者關掉自動調整（例如想固定在最高畫質截圖）。 */
export function setResolutionAuto(on: boolean, ratio?: number) {
  enabled = on;
  if (!on && ratio !== undefined) {
    const i = LEVELS.indexOf(ratio);
    if (i >= 0) { idx = i; apply(); }
  }
}
